import { subscribeAlmsyncCommon } from "@/natsstubs/almsync/common/subscribe";
import type { ApiSubscribe, ApiSubscriber, ApiSubscription } from "@/natsstubs/wire/ApiSubscriber";
import { captureExceptionWithContext } from "@/sentry";

export interface AlmStatusSubscriptions {
  subjects: string[];
  unsubscribe: () => Promise<void>;
}

/**
 * `NoInfer` pins T to the `subscribe` argument. With a bare T the handler
 * literal also drives inference, so a handler whose subject a respec renamed
 * would type-check while never being subscribed.
 */
function apiSubscribe<T>(
  subscriber: ApiSubscriber,
  subscribe: ApiSubscribe<T>,
  handler: NoInfer<T>,
  params: Record<string, string>,
): ApiSubscription[] {
  return subscribe(subscriber, params, handler);
}

function subscribeAlmStatus(
  subscriber: ApiSubscriber,
  almSessionId: string,
  onStatus: (status: string) => void,
): AlmStatusSubscriptions {
  const subscriptions = apiSubscribe(
    subscriber,
    subscribeAlmsyncCommon,
    {
      runningStateUpdated(event) {
        onStatus(event.status);
      },
    },
    { sessionId: almSessionId },
  );
  return {
    subjects: subscriptions.map((subscription) => subscription.getSubject()),
    unsubscribe: async () => {
      await Promise.all(subscriptions.map((subscription) => subscription.unsubscribe()));
    },
  };
}

/**
 * `NatsApiSubscriber.subscribe` throws synchronously when the connection is
 * unusable, which would skip the WAMP subscription registered after it. The
 * WAMP one is the fallback, so a NATS failure must never escape.
 */
export function subscribeAlmStatusSafely(
  subscriber: ApiSubscriber,
  almSessionId: string,
  onStatus: (status: string) => void,
): AlmStatusSubscriptions | null {
  try {
    return subscribeAlmStatus(subscriber, almSessionId, onStatus);
  } catch (error) {
    captureExceptionWithContext(error, { subscription: { almSessionId } });
    return null;
  }
}
